import { motion } from "framer-motion";
import { Crown, Globe, HandHeart, Sunrise, UtensilsCrossed } from "lucide-react";
import { EVENTS, IMAGES } from "../../data/content";
import { reveal } from "../../lib/motion";

const ICONS = {
  sunrise: Sunrise,
  utensils: UtensilsCrossed,
  crown: Crown,
  globe: Globe,
};

export const Legado = () => {
  return (
    <>
      {/* Seção 12 — Caridade & Legado */}
      <section
        data-testid="charity-section"
        className="grain-overlay bg-teal-deep py-16 md:py-24 lg:py-32"
      >
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <motion.p
              {...reveal()}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-champagne"
            >
              <HandHeart className="h-4 w-4" /> Caridade & Legado
            </motion.p>
            <motion.h2
              {...reveal(0.1)}
              className="mt-4 font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-5xl"
            >
              "Prosperar para viver. Viver para servir.{" "}
              <em className="italic text-champagne">Servir para deixar um legado."</em>
            </motion.h2>
            <motion.p
              {...reveal(0.2)}
              className="mt-6 text-base leading-relaxed text-white/80 sm:text-lg"
            >
              Parte de tudo o que o AURA gera retorna em ação social: projetos
              benevolentes, apoio a famílias, capacitação de mulheres em
              vulnerabilidade e cuidado com a comunidade. Porque prosperidade
              sem propósito é apenas acúmulo — e nós fomos chamadas para
              multiplicar.
            </motion.p>
            <motion.div {...reveal(0.3)} className="mt-10 space-y-5">
              {[
                ["100%", "Compromisso com o serviço"],
                ["4", "Frentes de ação social"],
                ["∞", "Legado que permanece"],
              ].map(([num, label]) => (
                <div key={label} className="flex items-baseline gap-4 border-l-2 border-champagne/60 pl-5">
                  <span className="font-serif text-3xl text-champagne sm:text-4xl">{num}</span>
                  <span className="text-sm uppercase tracking-[0.15em] text-white/70">{label}</span>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div {...reveal(0.2)} className="relative overflow-hidden rounded-3xl">
            <img
              src={IMAGES.charitySection}
              alt="Ação social do Ministério AURA"
              className="h-[380px] w-full object-cover sm:h-[480px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-deep/60 to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Seção 13 — Experiência AURA */}
      <section
        data-testid="events-section"
        className="bg-cream py-16 md:py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <motion.p
            {...reveal()}
            className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold"
          >
            A Experiência AURA
          </motion.p>
          <motion.h2
            {...reveal(0.1)}
            className="mt-4 text-center font-serif text-3xl text-ink sm:text-4xl lg:text-5xl"
          >
            Encontros que <em className="italic text-teal">marcam para sempre</em>
          </motion.h2>
          <motion.p
            {...reveal(0.2)}
            className="mx-auto mt-4 max-w-2xl text-center text-base text-mutedink sm:text-lg"
          >
            O AURA também acontece fora das telas — em mesas, galas e
            amanheceres de oração.
          </motion.p>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-8">
            {EVENTS.map((e, i) => {
              const Icon = ICONS[e.icon];
              return (
                <motion.div
                  key={e.title}
                  {...reveal(0.1 * (i + 1))}
                  data-testid={`event-card-${i + 1}`}
                  className="group overflow-hidden rounded-3xl border border-gold-border bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-gold hover:shadow-[0_20px_60px_rgba(197,160,89,0.12)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={IMAGES[e.image]}
                      alt={e.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 backdrop-blur-md">
                      <Icon className="h-5 w-5 text-champagne" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-ink">{e.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mutedink">{e.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
